import { useState, type ReactNode } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Alinhamento de cabeçalho e célula (números à direita). */
    align?: "left" | "right";
    /** Largura mínima em px da coluna. */
    minWidth?: number;
    /** Rótulo acessível quando o cabeçalho não é texto (menu, ícone). */
    label?: string;
    /** Controle extra no cabeçalho (menu da coluna), fora do botão de ordenar. */
    headerAction?: ReactNode;
  }
}

/**
 * Tabela de dados do painel (redesign 2026): cabeçalho cinza em caixa alta,
 * linhas de 13px com divisória, rolagem horizontal própria, primeira coluna
 * fixa opcional, ordenação por cabeçalho e seleção por checkbox. Não busca
 * nem pagina dados — recebe a página pronta; a paginação é o
 * `DataTablePagination` no `footer`. Linha não é clicável: a ação mora numa
 * célula (link ou botão), para teclado e leitor de tela.
 */
export interface DataTableProps<TData> {
  /** Nome da tabela para leitor de tela. */
  ariaLabel: string;
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  getRowId: (row: TData) => string;
  isLoading?: boolean;
  /** Renderizado no lugar das linhas quando a query falhou. */
  error?: ReactNode;
  /** Renderizado quando `data` chegou vazio (não durante loading). */
  empty?: ReactNode;
  /** Ordenação controlada; sem ela a tabela ordena a página localmente. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** `true` quando o pai ordena no servidor (a tabela só emite `onSortingChange`). */
  manualSorting?: boolean;
  /** Mostra a coluna de checkbox. */
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Fixa a primeira coluna de dados (depois do checkbox) na rolagem horizontal. */
  stickyFirstColumn?: boolean;
  /** Largura mínima em px — abaixo disso a tabela rola na horizontal. */
  minWidth?: number;
  /** Classe extra por linha (ex.: destaque de selecionada). */
  rowClassName?: (row: TData) => string | undefined;
  /** Rodapé (paginação, contagem). */
  footer?: ReactNode;
  className?: string;
}

const SELECT_COLUMN_ID = "__select";

export function DataTable<TData>({
  ariaLabel,
  columns,
  data,
  getRowId,
  isLoading = false,
  error,
  empty,
  sorting: sortingProp,
  onSortingChange,
  manualSorting = false,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  stickyFirstColumn = false,
  minWidth,
  rowClassName,
  footer,
  className,
}: DataTableProps<TData>) {
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const sorting = sortingProp ?? localSorting;

  const selectColumn: ColumnDef<TData, unknown> = {
    id: SELECT_COLUMN_ID,
    enableSorting: false,
    meta: { label: "Seleção" },
    header: ({ table }) => (
      <Checkbox
        aria-label="Selecionar todos da página"
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? "indeterminate"
              : false
        }
        onCheckedChange={(v) => table.toggleAllPageRowsSelected(v === true)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label="Selecionar linha"
        checked={row.getIsSelected()}
        onCheckedChange={(v) => row.toggleSelected(v === true)}
      />
    ),
  };

  const table = useReactTable({
    data,
    columns: enableRowSelection ? [selectColumn, ...columns] : columns,
    getRowId: (row) => getRowId(row),
    state: { sorting, rowSelection: rowSelection ?? {} },
    onSortingChange: onSortingChange ?? setLocalSorting,
    manualSorting,
    // 1º clique sempre ascendente (TanStack começa números por desc).
    sortDescFirst: false,
    enableRowSelection,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
  });

  const visibleColumns = table.getVisibleLeafColumns();
  const stickyIndex = stickyFirstColumn ? (enableRowSelection ? 1 : 0) : -1;
  const stickyLeft = enableRowSelection ? 44 : 0;

  const stickyClass = (index: number, isHeader: boolean) =>
    index === stickyIndex
      ? cn(
          "sticky z-[1] border-r border-border",
          isHeader ? "bg-muted" : "bg-card group-hover:bg-muted",
        )
      : index === 0 && stickyIndex === 1
        ? cn("sticky left-0 z-[1]", isHeader ? "bg-muted" : "bg-card group-hover:bg-muted")
        : undefined;

  let body: ReactNode;
  if (error) {
    body = (
      <tr>
        <td colSpan={visibleColumns.length} className="px-6 py-8">
          {error}
        </td>
      </tr>
    );
  } else if (isLoading) {
    body = Array.from({ length: 5 }, (_, i) => (
      <tr key={i} className="border-t border-border">
        {visibleColumns.map((col) => (
          <td key={col.id} className="px-3 py-[15px] first:pl-5">
            <Skeleton className="h-4 w-full max-w-[160px]" />
          </td>
        ))}
      </tr>
    ));
  } else if (table.getRowModel().rows.length === 0) {
    body = (
      <tr>
        <td colSpan={visibleColumns.length} className="px-6">
          {empty}
        </td>
      </tr>
    );
  } else {
    body = table.getRowModel().rows.map((row) => (
      <tr
        key={row.id}
        className={cn(
          "group border-t border-border text-[13px] hover:bg-muted/40",
          row.getIsSelected() && "bg-accent/40",
          rowClassName?.(row.original),
        )}
      >
        {row.getVisibleCells().map((cell, index) => (
          <td
            key={cell.id}
            className={cn(
              "px-3 py-3 align-middle first:pl-5",
              cell.column.id === SELECT_COLUMN_ID && "w-11 pr-0",
              cell.column.columnDef.meta?.align === "right" && "text-right tabular-nums",
              stickyClass(index, false),
            )}
            style={{
              minWidth: cell.column.columnDef.meta?.minWidth,
              ...(index === stickyIndex ? { left: stickyLeft } : {}),
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    ));
  }

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border border-border bg-card shadow-card",
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table aria-label={ariaLabel} className="w-full border-collapse" style={{ minWidth }}>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="bg-muted">
                {group.headers.map((header, index) => {
                  const meta = header.column.columnDef.meta;
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  const SortIcon =
                    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        sorted === "asc"
                          ? "ascending"
                          : sorted === "desc"
                            ? "descending"
                            : undefined
                      }
                      aria-label={meta?.label}
                      className={cn(
                        "whitespace-nowrap px-3 py-[11px] text-left text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground first:pl-5",
                        header.column.id === SELECT_COLUMN_ID && "w-11 pr-0",
                        meta?.align === "right" && "text-right",
                        stickyClass(index, true),
                      )}
                      style={{
                        minWidth: meta?.minWidth,
                        ...(index === stickyIndex ? { left: stickyLeft } : {}),
                      }}
                    >
                      <span className="inline-flex items-center gap-1">
                        {canSort ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className={cn(
                              "inline-flex items-center gap-1 uppercase hover:text-foreground",
                              sorted && "text-foreground",
                            )}
                          >
                            {content}
                            <SortIcon className="h-3 w-3" aria-hidden="true" />
                          </button>
                        ) : (
                          content
                        )}
                        {meta?.headerAction}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {footer}
    </section>
  );
}

/** Rodapé "N itens · página X de Y" com anterior/próxima. Página é 1-based. */
export interface DataTablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Substantivo no plural para a contagem ("leads frios"). */
  itemLabel?: string;
  className?: string;
}

export function DataTablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  itemLabel = "itens",
  className,
}: DataTablePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return (
    <footer
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-[12.5px] text-muted-foreground",
        className,
      )}
    >
      <span>
        {total} {itemLabel} · página {Math.min(page, pageCount)} de {pageCount}
      </span>
      <div className="flex gap-1.5">
        <button
          type="button"
          aria-label="Página anterior"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="grid h-[34px] w-[34px] place-items-center rounded-[9px] border border-border bg-card text-foreground hover:bg-muted disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Próxima página"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className="grid h-[34px] w-[34px] place-items-center rounded-[9px] border border-border bg-card text-foreground hover:bg-muted disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </footer>
  );
}
